import { debounce } from "lodash";
import { ReduxState, useListFilterContext, useListPaginationContext } from "ra-core";
import { Dispatch, FC, SetStateAction, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DataOwnerGroupResponseDto, PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { FileCatalogSearchInput, FileInstanceOptions, FileTagResponseDto } from "@/gen/api/securefile";
import { getStashedPageState } from "@/reducers/pageStash";
import { useStashPageStateWhenTransiteAway } from "@/utils/useStashPageStateWhenTransiteAway";


export const osfCatalogRes = 'secure-files/file-catalog'
export const pfsCatalogRes = 'secure-files/pfs'

export interface FileCataSearchStates {
  filters: {
    name: string | null;
    tags: FileTagResponseDto[];
    primaryClassification: (PrimaryClassification|null)[];
    secondClassification: SecondaryClassificationResponseDto[];
    documentTypes: Array<string>;
    infoTypes: Array<string>;
    fileInstanceOptions: FileInstanceOptions;
    ownerGroup: DataOwnerGroupResponseDto[];
  },
  currRes: string;
}
export interface FileCataSearchContextProps extends FileCataSearchStates {
  switchCurrRes: () => void;
  searchName: (s: string|null) => void;
  addSearchTag: (t: FileTagResponseDto) => void;
  removeSearchTag: (t: FileTagResponseDto) => void;
  addPrimaryClassification: (t: PrimaryClassification|null) => void;
  removePrimaryClassification: (t: PrimaryClassification|null) => void;
  addSecondClassification: (t: SecondaryClassificationResponseDto) => void;
  removeSecondClassification: (t: SecondaryClassificationResponseDto) => void;
  addDocumentType: (dt: string) => void;
  removeDocumentType: (dt: string) => void;
  addInfoType: (it: string) => void;
  removeInfoType: (it: string) => void;
  addOwnerGroup: (o: DataOwnerGroupResponseDto) => void;
  removeOwnerGroup: (o: DataOwnerGroupResponseDto) => void;
  addFileInstanceOption: (o: FileInstanceOptions) => void;
  removeFileInstanceOption: (o: FileInstanceOptions) => void;
}

export const FileCataSearchContext = createContext<FileCataSearchContextProps>({} as any)


export const useFileCataSearchStates = (initCurrRes?: string) => {
  const currRes = initCurrRes || osfCatalogRes
  const stashed = useSelector<ReduxState>(getStashedPageState('file-catalog')) as FileCataSearchStates
  const stashed1 = stashed && initCurrRes ? {...stashed, currRes} : stashed
  const [states, setStates] = useState<FileCataSearchStates>(() => {
    const initS = stashed1 ?? {
      currRes,
      filters: {
        name: null as string|null,
        tags: [] as FileTagResponseDto[],
        primaryClassification: [] as (PrimaryClassification|null)[],
        secondClassification: [] as SecondaryClassificationResponseDto[],
        documentTypes: [] as Array<string>,
        infoTypes: [] as Array<string>,
        fileInstanceOptions: FileInstanceOptions.All,
        ownerGroup: [] as DataOwnerGroupResponseDto[],
      }
    }
    return {...initS, currRes: 'noop'}
  })
  const { current: delayedInitialListQuery } = useRef(() => {
    setStates(v => ({...v, currRes: stashed1?.currRes ?? currRes}))
  })

  useStashPageStateWhenTransiteAway('file-catalog', () => states, [states])

  return { states, setStates, delayedInitialListQuery }
}


export const FileCataSearchContextProvider : FC<{ 
    states: FileCataSearchStates;
    setStates: Dispatch<SetStateAction<FileCataSearchStates>>;
    delayedInitialListQuery: () => void; 
}> = ({ children, states, setStates, delayedInitialListQuery }) => {
  const { setFilters, displayedFilters } = useListFilterContext()

  const ctrls = useMemo(() => {
    const invokeRequest = (s0: FileCataSearchStates['filters'], immediate?: boolean) => {
      const searchInput : FileCatalogSearchInput = {
        term: !s0.name ? null : s0.name.trim(),
        tags: s0.tags.map(x => x.tagId!),
        primaryClassifications: [...s0.primaryClassification] as any,
        secondaryClassifications: s0.secondClassification.map(x => x.secondaryClassificationId!),
        documentTypes: [...s0.documentTypes],
        infoTypes: [...s0.infoTypes],
        fileInstanceOptions: s0.fileInstanceOptions,
        dataOwnerGroupIds: s0.ownerGroup.map(o => o.ownerGroupId!),
      }
      if (immediate != null) {
        setFilters(searchInput, displayedFilters, !immediate)
      } else {
        setFilters(searchInput, displayedFilters)
      }
    }
    const debouncedInvokeReq = debounce((s0: FileCataSearchStates['filters']) => {
      invokeRequest(s0)
    }, 500)
    
    return {
      invokeRequest,
      searchName: (s:string|null) => {
        setStates(v => {
          if (v.filters.name !== s) {
            const v1 = {...v, filters: {...v.filters, name: s}}
            setTimeout(() => debouncedInvokeReq(v1.filters))
            return v1
          } else {
            return v
          }
        })
      },
      addSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.tags.every(x => x.tagId !== t.tagId)) {
            const f1 = {...f, tags: [...f.tags, t]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeSearchTag: (t:FileTagResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.tags.findIndex(x => x.tagId === t.tagId)
          if (i > -1) {
            const tags = [...f.tags]
            tags.splice(i, 1)
            const f1 = {...f, tags}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addPrimaryClassification: (t:PrimaryClassification|null) => {
        setStates(v => {
          const f = v.filters
          if (f.primaryClassification.every(x => x !== t)) {
            const f1 = {...f, primaryClassification: t == null ? [null] : [...f.primaryClassification.filter(x => x != null), t]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removePrimaryClassification: (t:PrimaryClassification|null) => {
        setStates(v => {
          const f = v.filters
          const i = f.primaryClassification.findIndex(x => x === t)
          if (i > -1) {
            const primaryClassification = [...f.primaryClassification]
            primaryClassification.splice(i, 1)
            const f1 = {...f, primaryClassification}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.secondClassification.every(x => x.secondaryClassificationId !== t.secondaryClassificationId)) {
            const f1 = {...f, secondClassification: [...f.secondClassification, t]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeSecondClassification: (t:SecondaryClassificationResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.secondClassification.findIndex(x => x.secondaryClassificationId === t.secondaryClassificationId)
          if (i > -1) {
            const secondClassification = [...f.secondClassification]
            secondClassification.splice(i, 1)
            const f1 = {...f, secondClassification}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addDocumentType: (dt: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.documentTypes.findIndex(x => x === dt)
          if (i < 0) {
            const f1 = {...f, documentTypes: [...f.documentTypes, dt]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeDocumentType: (dt: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.documentTypes.findIndex(x => x === dt)
          if (i > -1) {
            const documentTypes = [...f.documentTypes]
            documentTypes.splice(i, 1)
            const f1 = {...f, documentTypes}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addInfoType: (it: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.infoTypes.findIndex(x => x === it)
          if (i < 0) {
            const f1 = {...f, infoTypes: [...f.infoTypes, it]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      removeInfoType: (it: string) => {
        setStates(v => {
          const f = v.filters
          const i = f.infoTypes.findIndex(x => x === it)
          if (i > -1) {
            const infoTypes = [...f.infoTypes]
            infoTypes.splice(i, 1)
            const f1 = {...f, infoTypes}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          } else {
            return v
          }
        })
      },
      addFileInstanceOption: (o: FileInstanceOptions) => {
        setStates(v => {
          const f = v.filters
          if (o === f.fileInstanceOptions) {
            return v
          } else {
            const fileInstanceOptions = o
            const f1 = {...f, fileInstanceOptions }
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          }
        })
      },
      removeFileInstanceOption: () => {
        setStates(v => {
          const fileInstanceOptions = FileInstanceOptions.All
          const f = v.filters
          const f1 = {...f, fileInstanceOptions }
          setTimeout(() => invokeRequest(f1))
          return {...v, filters: f1}
        })
      },
      addOwnerGroup: (o: DataOwnerGroupResponseDto) => {
        setStates(v => {
          const f = v.filters
          if (f.ownerGroup.every(x => x.ownerGroupId !== o.ownerGroupId)) {
            const f1 = {...f, ownerGroup: [...f.ownerGroup, o]}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          }
          return v
        })
      },
      removeOwnerGroup: (o: DataOwnerGroupResponseDto) => {
        setStates(v => {
          const f = v.filters
          const i = f.ownerGroup.findIndex(x => x.ownerGroupId === o.ownerGroupId)
          if (i > -1) {
            const ownerGroup = [...f.ownerGroup]
            ownerGroup.splice(i, 1)
            const f1 = {...f, ownerGroup}
            setTimeout(() => invokeRequest(f1))
            return {...v, filters: f1}
          }
          return v
        })
      },
      switchCurrRes: () => {
        setStates(v => ({...v,
          currRes: v.currRes === osfCatalogRes
            ? pfsCatalogRes
            : osfCatalogRes
        }))
      }
    }
  }, [setFilters, displayedFilters, setStates])
  
  const stashedPager = useSelector<ReduxState, {page:number,perPage:number}|undefined>(getStashedPageState('file-cata/pager'))
  const listPageCtrl = useListPaginationContext()
  const {page, perPage} = listPageCtrl
  const listPageCtrlRef = useRef(listPageCtrl)
  listPageCtrlRef.current = listPageCtrl
  useEffect(() => {
    if (stashedPager) {
      if (listPageCtrlRef.current.perPage !== stashedPager.perPage) {
        listPageCtrlRef.current.setPerPage(stashedPager.perPage)
      }
      if (listPageCtrlRef.current.page !== stashedPager.page) {
        listPageCtrlRef.current.setPage(stashedPager.page)
      }
    }
    delayedInitialListQuery()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useStashPageStateWhenTransiteAway('file-cata/pager', () => {
    return ({page, perPage})
  }, [page, perPage])

  return (
    <FileCataSearchContext.Provider value={{...ctrls, ...states}}>
      {children}
    </FileCataSearchContext.Provider>
  )
}

export const useFileCataSearchContext = () => useContext(FileCataSearchContext)

